import { useTranslation } from "react-i18next";
import { DraggableWindow } from "@tellescope/ui";
import { DialerPanel, type DialerPanelProps } from "./DialerPanel";
import { VideoCallDialerPanel } from "./VideoCallDialerPanel";
import type { DialerMode } from "../../store/calling-store";

export interface DialerWindowProps {
  /** Whether the dialer window is open */
  open: boolean;
  /** Callback when the window open state changes */
  onOpenChange: (open: boolean) => void;
  /** Which dialer flow to render (defaults to "phone") */
  mode?: DialerMode;
  /** Optional enduser to pre-fill the phone dialer with */
  initialEnduser?: DialerPanelProps["initialEnduser"];
}

/**
 * DialerWindow - Draggable floating window for the phone or ad-hoc video call dialer.
 *
 * Wraps the relevant dialer panel in a DraggableWindow. The phone dialer auto-closes when a
 * call is placed (the CallWindow takes over); the video dialer closes itself once the call opens.
 */
export function DialerWindow({ open, onOpenChange, mode = "phone", initialEnduser }: DialerWindowProps) {
  const { t } = useTranslation("calling");
  const isVideo = mode === "video";

  return (
    <DraggableWindow
      open={open}
      onOpenChange={onOpenChange}
      title={isVideo ? t("Video call") : t("Phone call")}
      persistKey={isVideo ? "video-dialer-window" : "dialer-window"}
      width={isVideo ? 360 : 320}
      initialPosition="bottom-right"
      constrainToViewport
      viewportPadding={20}
    >
      {isVideo ? (
        <VideoCallDialerPanel onClose={() => onOpenChange(false)} />
      ) : (
        <DialerPanel
          onClose={() => onOpenChange(false)}
          onCallPlaced={() => onOpenChange(false)}
          initialEnduser={initialEnduser}
        />
      )}
    </DraggableWindow>
  );
}
